import { useQuery } from "@tanstack/react-query";
import { configService } from "@/app/services/configService";
import {
  DialogContent,
  DialogHeader,
} from "@/view/components/ui/dialog";
import { Spinner } from "@/view/components/ui/spinner";
import { PartnerType } from "@/app/utils/partners.enum";
import { ProductDetail } from "@/app/services/configService/getProductsConfig";
import { getPartnerBgColor, getPartnerImage } from "@/app/utils/partners";
import { TriStateEnum } from "@/app/entities/TriState";
import { ChevronRight } from "lucide-react";

const partnerDescriptions: Record<PartnerType, string> = {
  [PartnerType.FGTS]: "Antecipação do saque-aniversário do FGTS",
  [PartnerType.CREFAZ]: "Crédito pessoal com débito na fatura de energia",
  [PartnerType.SALVCAR]: "Proteção veicular",
  [PartnerType.CREFISA]: "Crédito pessoal",
  [PartnerType.PAN]: "Consignado privado",
};

export default function ChooseSimulationModal({
  onOptionSelect,
}: {
  onOptionSelect: (option: PartnerType) => void;
}) {
  const { data: productsConfig, isLoading } = useQuery({
    queryKey: ["productsConfig"],
    queryFn: configService.getProductsConfig,
  });

  const simulationOptions = productsConfig
    ? (
        Object.entries(productsConfig.products) as [
          PartnerType,
          ProductDetail,
        ][]
      )
        .filter(([, detail]) => detail.state === TriStateEnum.FULL)
        .map(([productName]) => productName)
    : [];

  if (isLoading) {
    return (
      <DialogContent className="w-full max-w-3xl">
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="w-full max-w-3xl p-6">
      <DialogHeader className="pb-2"></DialogHeader>

      <div className="flex flex-col gap-3">
        {simulationOptions.map((option) => (
          <div
            key={option}
            onClick={() => onOptionSelect(option)}
            className="flex cursor-pointer items-center gap-4 rounded-lg border border-gray01 p-3 transition-all hover:border-primary hover:shadow-sm"
          >
            <div
              className="flex h-14 w-24 shrink-0 items-center justify-center rounded-md"
              style={{ backgroundColor: getPartnerBgColor(option) }}
            >
              <img
                src={getPartnerImage(option)}
                alt={option}
                className={`object-scale-down ${
                  option === PartnerType.SALVCAR
                    ? "h-10 w-auto px-2"
                    : option === PartnerType.FGTS
                      ? "h-16 w-auto"
                      : "h-14 w-auto"
                }`}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-base font-semibold text-black01">
                {option === PartnerType.PAN
                  ? "Banco PAN"
                  : option.charAt(0) + option.slice(1).toLowerCase()}
              </span>
              <span className="text-sm text-black03">
                {partnerDescriptions[option]}
              </span>
            </div>
            <ChevronRight className="ml-auto h-5 w-5 text-black04" />
          </div>
        ))}
      </div>
    </DialogContent>
  );
}
